/**
 * Registry of course module kinds (F2-A) — same shape as BlockRegistry
 * (`learning-blocks/registry.ts`): authoring/player look up HERE the plugin
 * for each `kind`; adding a new kind is `registry.register(plugin)` without
 * touching the core. It's the piece that makes the Moodle spine extensible
 * (F3 registers assignment/forum/video/live_session with no core edits).
 */

import type { FeatureManifest } from "../country/packs";
import type { CourseModulePlugin } from "./types";

export class CourseModuleRegistry<TComponent = unknown> {
  private readonly plugins = new Map<string, CourseModulePlugin<TComponent>>();

  /** Registers a plugin. Throws if the `kind` already exists (avoids overwriting kinds). */
  register(plugin: CourseModulePlugin<TComponent>): this {
    if (this.plugins.has(plugin.kind)) {
      throw new Error(`CourseModuleRegistry: kind duplicado "${plugin.kind}"`);
    }
    if (plugin.manifest.key !== `academy.module.${plugin.kind}`) {
      throw new Error(
        `CourseModuleRegistry: manifest.key "${plugin.manifest.key}" no sigue la convención "academy.module.${plugin.kind}"`,
      );
    }
    if (!plugin.manifest.name || !plugin.manifest.description) {
      throw new Error(
        `CourseModuleRegistry: manifest de "${plugin.kind}" incompleto (name/description obligatorios — ninguna feature oculta)`,
      );
    }
    this.plugins.set(plugin.kind, plugin);
    return this;
  }

  /** Registers several at once. */
  registerAll(plugins: readonly CourseModulePlugin<TComponent>[]): this {
    for (const p of plugins) {
      this.register(p);
    }
    return this;
  }

  get(kind: string): CourseModulePlugin<TComponent> | undefined {
    return this.plugins.get(kind);
  }

  has(kind: string): boolean {
    return this.plugins.has(kind);
  }

  /** Registered kinds (for the authoring "add module" menu). */
  kinds(): string[] {
    return [...this.plugins.keys()];
  }

  /** Alias for `kinds()` — parity with BlockRegistry.types(). */
  types(): string[] {
    return this.kinds();
  }

  /**
   * Manifests of every kind (self-describing catalog → `gen:features`).
   */
  manifests(): FeatureManifest[] {
    return [...this.plugins.values()].map((p) => p.manifest);
  }

  /**
   * Kinds enabled according to `course_settings.enabled_module_kinds` (JSON
   * array or null). `null`/invalid = ALL registered ones; a list filters
   * (only actually registered kinds).
   */
  enabledKinds(enabledModuleKinds: string | null | undefined): string[] {
    const all = this.kinds();
    if (!enabledModuleKinds) {
      return all;
    }
    try {
      const parsed: unknown = JSON.parse(enabledModuleKinds);
      if (!Array.isArray(parsed)) {
        return all;
      }
      const wanted = new Set(parsed.filter((k): k is string => typeof k === "string"));
      return all.filter((k) => wanted.has(k));
    } catch {
      return all;
    }
  }
}
